import { Button, Stack, Table, TableCell, TableRow, Typography } from '@mui/material';

import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import type { YieldEstimationAPI } from '../..';

const YieldEstimationResult = ({
    yieldEstimation,
    goBack,
    onSave,
    saving = false,
    showSave = true,
}: {
    goBack: () => void;
    yieldEstimation: YieldEstimationAPI;
    /** Persist the estimation (create or update). Only invoked when `showSave`. */
    onSave: () => void;
    saving?: boolean;
    /** Show the "Guardar" action — only after the user has (re)run an estimation this session. */
    showSave?: boolean;
}) => {
    const units = useUnitProvider();

    return (
        <>
            <Stack
                p={{ xs: 2, sm: 3 }}
                spacing={4}
                overflow={'auto'}
                // Takes the remaining drawer height so the actions stay pinned to the bottom.
                flex={1}
                minHeight={0}
            >
                <Stack spacing={1}>
                    <Typography color={'primary'} variant='subtitle1' fontWeight={'bold'}>
                        {_('Rendimiento estimado')}
                    </Typography>
                    <Typography color={'primary'} variant='h6'>
                        {units.toWeightPerArea(Number(yieldEstimation.result) || 0, 2).toString()}
                    </Typography>
                </Stack>

                <Stack spacing={3}>
                    <Typography color={'primary'} variant='subtitle2'>
                        {_('Rendimiento estimado')}
                    </Typography>
                    <Table
                        sx={{
                            fontSize: (t) => t.typography.body2.fontSize,
                            fontFamily: (t) => t.typography.body2.fontFamily,
                            color: (t) => t.palette.primary.main,
                            border: (t) => `1px solid ${t.palette.divider}`,

                            '& .MuiTableCell-root': {
                                fontFamily: (t) => t.typography.body2.fontFamily,
                                px: { xs: 1, sm: 2 },
                            },
                        }}
                    >
                        <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 'bold' } }}>
                            <TableCell>{_('Muestra')}</TableCell>
                            <TableCell>{_('Nombre')}</TableCell>
                            <TableCell>{_('Rendimiento estimado')}</TableCell>
                        </TableRow>
                        {yieldEstimation.yieldSamples.map((el, index) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: fixed, non-reordered list; preview samples have no id
                            <TableRow key={index}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{`${_('Muestra')} ${index + 1}`}</TableCell>
                                <TableCell>{el.name}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {units.toWeightPerArea(Number(el.result), 2).toString()}
                                </TableCell>
                            </TableRow>
                        ))}
                    </Table>
                </Stack>
            </Stack>
            <Stack
                direction={'row'}
                justifyContent={'flex-end'}
                spacing={2}
                p={2}
                borderTop={(t) => `1px solid ${t.palette.divider}`}
            >
                <Button
                    variant='text'
                    color='secondary'
                    onClick={() => goBack()}
                    disabled={saving}
                    sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                    {_('Editar')}
                </Button>
                {showSave && (
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={onSave}
                        disabled={saving}
                        sx={{ flex: { xs: 1, sm: 'none' } }}
                    >
                        {_('Guardar')}
                    </Button>
                )}
            </Stack>
        </>
    );
};

export default YieldEstimationResult;
